import { MW, MH, COL_HEX } from './config.js';
import S from './state.js';
import { BURST_FAMILY, DUAL_WIELD_FAMILY, MAG_SIZES, EXT_MAG_SIZES } from '../shared/constants.js';
import { getTransportKind, send } from './network.js';
import { clampFireMode } from './input.js';

const _escapeHtml = s => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Cache all HUD element refs at first invocation instead of calling getElementById every frame
let H = null;
function initHudRefs() {
  H = {
    fpsCounter: document.getElementById('fpsCounter'),
    weapon: document.getElementById('weapon'),
    hunger: document.getElementById('hunger'),
    hungerFill: document.getElementById('hungerFill'),
    hungerTxt: document.getElementById('hungerTxt'),
    xpBar: document.getElementById('xpBar'),
    xpFill: document.getElementById('xpFill'),
    xpTxt: document.getElementById('xpTxt'),
    dashBar: document.getElementById('dashBar'),
    dashFill: document.getElementById('dashFill'),
    atkBar: document.getElementById('atkBar'),
    atkFill: document.getElementById('atkFill'),
    armorBar: document.getElementById('armorBar'),
    armorFill: document.getElementById('armorFill'),
    armorTxt: document.getElementById('armorTxt'),
    crosshair: document.getElementById('crosshair'),
    chN: document.getElementById('chN'),
    chS: document.getElementById('chS'),
    chE: document.getElementById('chE'),
    chW: document.getElementById('chW'),
    barricadeBar: document.getElementById('barricadeBar'),
    barricadeFill: document.getElementById('barricadeFill'),
    barricadeLabel: document.getElementById('barricadeLabel'),
    spectateMsg: document.getElementById('spectateMsg'),
    playerCount: document.getElementById('playerCount'),
    chatLog: document.getElementById('chatLog'),
    minimap: document.getElementById('minimap'),
    lowHealthOverlay: document.getElementById('lowHealthOverlay'),
    spawnProtOverlay: document.getElementById('spawnProtOverlay'),
  };
  // Create minigun spin bar dynamically
  const spinBar = document.createElement('div');
  spinBar.id = 'minigunSpinBar';
  spinBar.style.cssText = 'position:fixed;bottom:120px;left:50%;transform:translateX(-50%);width:200px;display:none;z-index:50;pointer-events:none;';
  const spinTrack = document.createElement('div');
  spinTrack.id = 'minigunSpinTrack';
  spinTrack.style.cssText = 'width:100%;height:12px;background:rgba(0,0,0,0.6);border:1px solid #666;border-radius:3px;overflow:hidden;transition:box-shadow 0.15s;';
  const spinFill = document.createElement('div');
  spinFill.id = 'minigunSpinFill';
  spinFill.style.cssText = 'height:100%;width:0%;background:linear-gradient(90deg,#ff4400,#ffaa00);border-radius:2px;transition:width 0.08s linear,background 0.15s;';
  spinTrack.appendChild(spinFill);
  spinBar.appendChild(spinTrack);
  const spinLabel = document.createElement('div');
  spinLabel.id = 'minigunSpinLabel';
  spinLabel.style.cssText = 'color:#ffaa00;font-size:10px;text-align:center;margin-top:2px;letter-spacing:1px;font-weight:bold;text-shadow:0 0 3px rgba(0,0,0,0.8);transition:color 0.15s;';
  spinLabel.textContent = 'SPIN UP [RMB]';
  spinBar.appendChild(spinLabel);
  // Inject the pulse keyframes once
  if (!document.getElementById('minigunSpinKeys')) {
    const style = document.createElement('style');
    style.id = 'minigunSpinKeys';
    style.textContent = '@keyframes minigunReadyPulse{0%,100%{box-shadow:0 0 4px rgba(0,255,68,0.5),inset 0 0 4px rgba(0,255,68,0.3);}50%{box-shadow:0 0 12px rgba(0,255,68,0.95),inset 0 0 6px rgba(0,255,68,0.5);}}';
    document.head.appendChild(style);
  }
  document.body.appendChild(spinBar);
  H.spinBar = spinBar;
  H.spinTrack = spinTrack;
  H.spinFill = spinFill;
  H.spinLabel = spinLabel;
  H._spinPrev = 0;
  H._spinState = 'idle';
}

export function updateHud(me, time, dt) {
  if (!H) initHudRefs();
  // FPS counter
  S.fpsFrames++;
  const fpsNow = performance.now();
  if (fpsNow - S.fpsLast >= 1000) { S.fpsDisplay = S.fpsFrames; S.fpsFrames = 0; S.fpsLast = fpsNow; }
  H.fpsCounter.textContent = S.fpsDisplay + 'fps | ' + Math.round(S.pingVal) + 'ms';

  // Hide alive-only HUD elements when spectating
  const aliveHud = me && me.alive;
  const aliveDisp = aliveHud ? '' : 'none';
  if (S._aliveDisp !== aliveDisp) {
    S._aliveDisp = aliveDisp;
    H.weapon.style.display = aliveDisp;
    H.hunger.style.display = aliveDisp;
    H.xpBar.style.display = 'none'; // Perks/XP temporarily disabled
    H.dashBar.style.display = aliveDisp;
    H.atkBar.style.display = aliveDisp;
    H.crosshair.style.display = aliveDisp;
    H.barricadeBar.style.display = aliveDisp;
    H.barricadeLabel.style.display = aliveDisp;
  }

  // Minigun spin-up bar
  if (H.spinBar) {
    const showSpin = aliveHud && me.weapon === 'minigun';
    H.spinBar.style.display = showSpin ? 'block' : 'none';
    if (showSpin) {
      const spin = me.minigunSpin || 0;
      const pct = Math.min(100, spin * 100);
      H.spinFill.style.width = pct + '%';
      // Direction detection: ready (≥0.99), spinning up, spinning down, idle
      const dSpin = spin - H._spinPrev;
      let nextState;
      if (spin >= 0.99) nextState = 'ready';
      else if (spin <= 0.001) nextState = 'idle';
      else if (dSpin > 0.0005) nextState = 'up';
      else if (dSpin < -0.0005) nextState = 'down';
      else nextState = H._spinState; // sticky if no movement
      if (nextState !== H._spinState) {
        H._spinState = nextState;
        if (nextState === 'ready') {
          H.spinFill.style.background = '#00ff44';
          H.spinTrack.style.animation = 'minigunReadyPulse 0.6s ease-in-out infinite';
          H.spinLabel.textContent = 'READY [LMB]';
          H.spinLabel.style.color = '#00ff44';
        } else if (nextState === 'down') {
          H.spinFill.style.background = 'linear-gradient(90deg,#3399ff,#88ccff)';
          H.spinTrack.style.animation = '';
          H.spinTrack.style.boxShadow = '';
          H.spinLabel.textContent = 'SPIN DOWN';
          H.spinLabel.style.color = '#88ccff';
        } else if (nextState === 'up') {
          H.spinFill.style.background = 'linear-gradient(90deg,#ff4400,#ffaa00)';
          H.spinTrack.style.animation = '';
          H.spinTrack.style.boxShadow = '';
          H.spinLabel.textContent = 'SPINNING UP...';
          H.spinLabel.style.color = '#ffaa00';
        } else { // idle
          H.spinFill.style.background = 'linear-gradient(90deg,#ff4400,#ffaa00)';
          H.spinTrack.style.animation = '';
          H.spinTrack.style.boxShadow = '';
          H.spinLabel.textContent = 'SPIN UP [RMB]';
          H.spinLabel.style.color = '#ffaa00';
        }
      }
      H._spinPrev = spin;
    } else if (H._spinState !== 'idle') {
      // Reset state when bar is hidden so next show starts clean
      H._spinState = 'idle';
      H._spinPrev = 0;
      H.spinTrack.style.animation = '';
      H.spinTrack.style.boxShadow = '';
    }
  }

  // Chat log render runs FIRST so spectators (and post-corpse-reap
  // dead players where me === null) still see chat. Throttled to 10 Hz.
  // Skip the rebuild entirely when there's nothing to show — common
  // case during early-round silence.
  if (!S._hudTick) S._hudTick = 0;
  S._hudTick += dt;
  if (S._hudTick >= 0.1 && S.chatLog.length > 0) {
    const tickDt = S._hudTick;
    S._hudTick = 0;
    for (let i = S.chatLog.length - 1; i >= 0; i--) {
      S.chatLog[i].t -= tickDt;
      if (S.chatLog[i].t <= 0) S.chatLog.splice(i, 1);
    }
    const chatEl = H.chatLog;
    if (chatEl) {
      // Build a signature from message count + first/last text to skip
      // innerHTML rebuild when content hasn't changed (opacity drift only)
      const chatSig = S.chatLog.length + '|' + (S.chatLog[0] ? S.chatLog[0].text : '') + '|' + (S.chatLog.length > 1 ? S.chatLog[S.chatLog.length-1].text : '');
      if (chatSig !== S._chatSig) {
        S._chatSig = chatSig;
        chatEl.innerHTML = S.chatLog.map(c => {
          const opacity = Math.min(1, c.t / 3);
          if (c.system) {
            return '<div style="margin-bottom:2px;opacity:' + opacity + ';color:#ddd">' + c.text + '</div>';
          }
          const col = COL_HEX[c.color] || '#ff88aa';
          return '<div style="margin-bottom:2px;opacity:' + opacity + '"><span style="color:' + col + ';font-weight:bold">' + _escapeHtml(c.name) + ':</span> ' + _escapeHtml(c.text) + '</div>';
        }).join('');
      }
    }
  } else if (S._hudTick >= 0.1) {
    // Empty chat — still flip the throttle counter so the minimap
    // (gated on the same `_hudTick === 0` signal further down) gets
    // its 10 Hz pulse.
    S._hudTick = 0;
    if (H.chatLog && H.chatLog.innerHTML !== '') H.chatLog.innerHTML = '';
  }

  if (!me) return;
  const hPct = Math.max(0, me.hunger / 100);
  H.hungerFill.style.width = (hPct * 100) + '%';
  H.hungerFill.style.background = hPct > 0.5 ? '#ffffff' : hPct > 0.25 ? '#dddddd' : '#ff4444';
  H.hungerTxt.textContent = 'MILK ' + Math.ceil(me.hunger) + '%';
  const wep = me.weapon || 'normal';
  const wepNames = { shotgun: 'XM1014', burst: 'M16A2', bolty: 'L96', cowtank: 'M72 LAW', normal: 'P250', aug: 'AUG', mp5k: 'MP5K', thompson: 'Thompson', sks: 'SKS', akm: 'AK', knife: 'Knife' };
  // Compute ammo numbers + flash class for the persistent ammo span.
  let ammoText = '', ammoCls = '';
  if (wep === 'cowtank') {
    ammoText = '1/1';
  } else if (me.ammo >= 0) {
    const hasExt = (me.extMagMult || 1) > 1;
    const baseMag = (hasExt ? EXT_MAG_SIZES[wep] : MAG_SIZES[wep]) || 0;
    const dualMult = (me.dualWield && DUAL_WIELD_FAMILY.has(wep)) ? 2 : 1;
    const maxMag = baseMag * dualMult;
    ammoText = me.ammo + '/' + maxMag;
    // Auto-reload at 0. Server validates + no-ops dupes (player.reloading > 0
    // gate in handleReload), but throttle so we don't spam the move queue
    // while the reload-start ack is in flight (~RTT/2).
    if (me.alive && me.ammo <= 0 && !me.reloading && wep !== 'minigun' && maxMag > 0) {
      const now = performance.now();
      if (!S._autoReloadAt || now - S._autoReloadAt > 300) {
        S._autoReloadAt = now;
        send({ type: 'reload' });
      }
    }
    // Two-stage low-ammo warning: mild pulse at ≤50%, hard strobe at ≤25%.
    // Empty mag + reload keep strobing without restarting — the ammo span is
    // updated via textContent/className below so the CSS animation isn't
    // remounted by the reload bar's per-frame innerHTML rewrites.
    const ammoFrac = maxMag > 0 ? me.ammo / maxMag : 1;
    ammoCls = ammoFrac <= 0.25 ? 'ammoCrit' : ammoFrac <= 0.5 ? 'ammoLow' : '';
  }
  // Reload progress bar — tracked client-side; HTML lives in its own slot
  // so the per-frame % update doesn't remount sibling DOM (and reset the
  // ammo flash animation).
  let reloadBlock = '';
  if (me.ammo >= 0 && me.reloading) {
    // Shotgun reloads shell-by-shell (750 ms each on the server). Reset the
    // bar each time a shell lands so it visualizes "time to next shell,"
    // not "time to full mag." Other weapons reload as one fixed-duration block.
    if (wep === 'shotgun' && S._reloadLastAmmo != null && me.ammo !== S._reloadLastAmmo) {
      S._reloadStart = null;
    }
    if (!S._reloadStart) {
      S._reloadStart = performance.now();
      const RELOAD_MS = { burst: 3000, mp5k: 3000, thompson: 3000, sks: 2500, akm: 3000, aug: 3500, bolty: 2500, normal: 2000 };
      const reloadMult = me.dualWield ? 2 : 1;
      S._reloadDuration = wep === 'shotgun' ? 750 : (RELOAD_MS[wep] || 2000) * reloadMult;
    }
    S._reloadLastAmmo = me.ammo;
    const elapsed = performance.now() - S._reloadStart;
    const pct = Math.min(100, (elapsed / S._reloadDuration) * 100);
    reloadBlock =
      '<div style="color:#ffaa44;font-size:0.35em;margin-bottom:4px;line-height:1">RELOADING...</div>' +
      '<div style="width:260px;height:10px;background:rgba(0,0,0,0.6);border-radius:3px;margin:0 0 8px auto">' +
        '<div style="height:100%;border-radius:3px;background:#ffaa44;width:' + pct + '%"></div>' +
      '</div>';
  } else {
    S._reloadStart = null;
    S._reloadDuration = null;
    S._reloadLastAmmo = null;
  }
  // Snap fireMode into a mode the current weapon actually supports.
  // The whitelist + priority lives in input.js (FIRE_MODES).
  clampFireMode(wep);
  let fireModeBlock = '';
  if (BURST_FAMILY.has(wep) && wep !== 'thompson') {
    const modeLabel = S.fireMode === 'auto' ? 'AUTO' : S.fireMode === 'semi' ? 'SEMI' : 'BURST';
    fireModeBlock = '<div>' + modeLabel + '</div>';
  }
  const dualTag = me.dualWield ? ' ×2' : '';
  // Lazy DOM scaffold: split #weapon into two stable children so the reload
  // bar can update freely without nuking the persistent ammo span.
  if (!S._weaponDomReady) {
    H.weapon.innerHTML = '<div data-slot="reload"></div><div data-slot="body"></div>';
    S._weaponReloadEl = H.weapon.firstChild;
    S._weaponBodyEl = H.weapon.lastChild;
    S._weaponDomReady = true;
    S._reloadHtml = '';
    S._bodySig = '';
  }
  if (S._reloadHtml !== reloadBlock) {
    S._reloadHtml = reloadBlock;
    S._weaponReloadEl.innerHTML = reloadBlock;
  }
  const bodySig = wep + '|' + dualTag + '|' + fireModeBlock;
  if (S._bodySig !== bodySig) {
    S._bodySig = bodySig;
    S._weaponBodyEl.innerHTML = fireModeBlock + (wepNames[wep] || wep) + dualTag + ' <span data-slot="ammo"></span>';
    S._ammoSpan = S._weaponBodyEl.querySelector('[data-slot="ammo"]');
  }
  if (S._ammoSpan) {
    if (S._ammoSpan.textContent !== ammoText) S._ammoSpan.textContent = ammoText;
    if (S._ammoSpan.className !== ammoCls) S._ammoSpan.className = ammoCls;
  }
  const armorVal = me.armor || 0;
  H.armorBar.style.display = (aliveHud && armorVal > 0) ? 'block' : 'none';
  H.armorFill.style.width = Math.min(100, armorVal) + '%';
  H.armorTxt.textContent = 'SHIELD ' + Math.ceil(armorVal);
  const xpPct = me.xpToNext > 0 ? Math.max(0, Math.min(100, (me.xp || 0) / (me.xpToNext || 50) * 100)) : 0;
  H.xpFill.style.width = xpPct + '%';
  H.xpTxt.textContent = 'LV' + (me.level || 0) + ' ' + Math.floor(me.xp || 0) + '/' + (me.xpToNext || 50) + ' XP';
  H.lowHealthOverlay.style.display = me.hunger < 30 ? 'block' : 'none';
  H.lowHealthOverlay.style.opacity = me.hunger < 30 ? Math.min(1, (30 - me.hunger) / 30 * (0.5 + Math.sin(time * 4) * 0.2)) : '0';
  if (H.spawnProtOverlay) {
    H.spawnProtOverlay.style.display = me.spawnProt ? 'block' : 'none';
    if (me.spawnProt) H.spawnProtOverlay.style.opacity = 0.3 + Math.sin(time * 6) * 0.1;
  }

  // Dash cooldown bar
  const dashMax = 3 * (me.dashCdMult || 1);
  const dashPct = me.dashCooldown > 0 ? Math.min(100, (me.dashCooldown / dashMax) * 100) : 0;
  H.dashFill.style.width = (100 - dashPct) + '%';
  H.dashFill.style.background = dashPct > 0 ? '#225588' : '#44aaff';
  // Barricade cooldown bar
  const nowMs = performance.now();
  const remaining = Math.max(0, S.barricadeReadyAt - nowMs);
  const barrPct = remaining > 0 ? 100 - Math.min(100, (remaining / 5000) * 100) : 100;
  H.barricadeFill.style.width = barrPct + '%';
  H.barricadeFill.style.background = remaining > 0 ? '#663322' : '#aa6633';
  // Attack cooldown — dynamically capture the peak of each cooldown window
  if (me.attackCooldown > (S._atkCdMax || 0)) S._atkCdMax = me.attackCooldown;
  if (me.attackCooldown <= 0) S._atkCdMax = 0;
  const atkMax = S._atkCdMax || 1;
  const atkPct = me.attackCooldown > 0 ? Math.min(100, (me.attackCooldown / atkMax) * 100) : 0;
  H.atkFill.style.width = (100 - atkPct) + '%';
  H.atkFill.style.background = atkPct > 0 ? '#882222' : '#ff6644';

  // Dynamic crosshair — spread per weapon, tightens when crouched, widens on movement/reload
  if (H.chN && aliveHud) {
    // AUG hipfire spread is 2.25x the M16 equivalent (penalty for not
    // using the optic). ADS would bring it back to baseline.
    const augBase = (S.fireMode === 'auto' ? 18 : 8) * 2.25;
    // MP5K hipfire spread is 2x LR (already baked into the stats), crosshair follows
    const mp5kBase = S.fireMode === 'auto' ? 36 : 16;
    const baseSpread = { normal: 28, shotgun: 42, bolty: 5, cowtank: 10, burst: S.fireMode === 'auto' ? 18 : 8, aug: augBase, mp5k: mp5kBase, thompson: 40, sks: 12, akm: S.fireMode === 'auto' ? 24 : 10 }[wep] || 8;
    const crouchMult = S.crouching ? 0.35 : 1;
    const movingMult = (S.keys['KeyW'] || S.keys['KeyS'] || S.keys['KeyA'] || S.keys['KeyD']) ? 2.2 : 1;
    const reloadMult = me.reloading ? 2.6 : 1;
    // Fire bloom — crosshair widens briefly during attack cooldown
    const fireMult = me.attackCooldown > 0 ? 1.6 : 1;
    const spread = Math.round(baseSpread * crouchMult * movingMult * reloadMult * fireMult);
    H.chN.style.marginTop = (-spread - 8) + 'px';
    H.chS.style.marginTop = spread + 'px';
    H.chE.style.marginLeft = spread + 'px';
    H.chW.style.marginLeft = (-spread - 8) + 'px';
  }
  const specEl = H.spectateMsg;
  if (me && me.alive) {
    if (specEl.style.display !== 'none') specEl.style.display = 'none';
  } else {
    if (specEl.style.display !== 'block') specEl.style.display = 'block';
    // Only rebuild the spectate HTML when the tracked target actually changes
    const target = S.serverPlayers.find(p => p.id === S.spectateTargetId);
    const signature = (S.killerName || '') + '|' + (target ? target.name : '');
    if (S._specSignature !== signature) {
      S._specSignature = signature;
      let html = '';
      if (S.killerName) html += '<div style="color:#ff4444;font-size:22px;font-weight:bold;margin-bottom:6px">\u{1F480} KILLED BY ' + S.killerName.toUpperCase() + '</div>';
      if (target) {
        html += '<div style="color:#ffdd44;font-size:20px;font-weight:bold">\u{1F441} SPECTATING: ' + target.name + '</div>';
        html += '<div style="font-size:12px;color:#aaccff;margin-top:4px">click / \u2190 \u2192 to switch target</div>';
      } else {
        html += '<div>SPECTATING - waiting for next round</div>';
      }
      specEl.innerHTML = html;
    }
  }
  // Player count — walk once, sig-gate the DOM write
  let _aliveCount = 0;
  for (let i = 0; i < S.serverPlayers.length; i++) if (S.serverPlayers[i].alive) _aliveCount++;
  const pcSig = _aliveCount + '/' + S.serverPlayers.length;
  if (S._pcSig !== pcSig) { S._pcSig = pcSig; H.playerCount.textContent = '\u{1F404} ' + pcSig; }

  // Debug overlay
  let dbg = document.getElementById('debugOverlay');
  if (!dbg && S.debugMode) {
    dbg = document.createElement('div'); dbg.id = 'debugOverlay';
    dbg.style.cssText = 'position:absolute;top:40px;left:10px;font-size:11px;color:#44ff44;text-shadow:1px 1px #000;font-family:monospace;white-space:pre;z-index:20;pointer-events:none';
    document.getElementById('hud').appendChild(dbg);
  }
  if (dbg) {
    dbg.style.display = S.debugMode ? 'block' : 'none';
    if (S.debugMode && me) {
      const yawDeg = ((S.yaw * 180 / Math.PI) % 360).toFixed(1);
      const pitchDeg = (S.pitch * 180 / Math.PI).toFixed(1);
      // Inchworm movement diagnostics — only relevant when debugging stuttery motion.
      // gaps: time between server 'state' messages (should be ~50ms at 20Hz, tight distribution)
      // deltas: per-message distance our player moved in the authoritative state
      // frameGaps: time between render frames (should be ~16ms at 60fps)
      // The underlying storage is a ring buffer; we walk only the valid prefix (count).
      let iwLine = '';
      if (S._iwStats) {
        const fmtRing = (buf, count) => {
          if (!buf || count === 0) return 'n/a';
          let min = Infinity, max = -Infinity, sum = 0;
          for (let i = 0; i < count; i++) { const v = buf[i]; if (v < min) min = v; if (v > max) max = v; sum += v; }
          return min.toFixed(0) + '/' + (sum / count).toFixed(0) + '/' + max.toFixed(0);
        };
        const iw = S._iwStats;
        iwLine = '\nSTATE gap ms: ' + fmtRing(iw.gaps, iw.gapsCount) +
                 '\nPOS delta: ' + fmtRing(iw.deltas, iw.deltasCount) +
                 '\nFRAME gap ms: ' + fmtRing(iw.frameGaps, iw.frameGapsCount) +
                 '\nJANK frames (>50ms): ' + iw.frameJank;
      }
      // Net stats — tick / ack / reconcile health for diagnosing
      // walking-rubberband when ping looks fine but jitter or sub-
      // epsilon drift is doing damage.
      let netLine = '';
      const ns = S.netStats;
      if (ns) {
        const tickRcv = ns._lastTickRcv || 0;
        const tickGap = ns._lastTickGap || 0;
        const tickRcvPct = tickRcv > 0 ? Math.round((tickRcv / (tickRcv + tickGap)) * 100) : 0;
        // Tick jitter = stddev of inter-arrival times.
        let jitter = 0;
        if (ns.tickGaps.length > 1) {
          let mean = 0; for (const g of ns.tickGaps) mean += g; mean /= ns.tickGaps.length;
          let variance = 0; for (const g of ns.tickGaps) { const d = g - mean; variance += d * d; }
          jitter = Math.sqrt(variance / ns.tickGaps.length);
        }
        // Reconcile snap stats.
        let snaps = 0, totalDrift = 0;
        for (const r of ns.reconcileSnapsWindow) {
          if (r.snapped) snaps++;
          totalDrift += r.drift;
        }
        const avgDrift = ns.reconcileSnapsWindow.length > 0
          ? (totalDrift / ns.reconcileSnapsWindow.length).toFixed(2)
          : '0';
        netLine =
          '\nTICK rcv: ' + tickRcvPct + '% (' + tickRcv + '/' + (tickRcv + tickGap) + ')' +
          ' jit=' + jitter.toFixed(1) + 'ms' +
          '\nRECONCILE: ' + snaps + ' snaps, drift avg ' + avgDrift + 'u';
      }
      dbg.textContent =
        'POS: ' + me.x.toFixed(0) + ', ' + me.y.toFixed(0) + ', ' + (me.z || 0).toFixed(1) +
        '\nAIM: yaw=' + yawDeg + ' pitch=' + pitchDeg +
        '\nWEP: ' + (me.weapon || 'normal') + ' ammo=' + (me.ammo >= 0 ? me.ammo : '\u221E') + (S.fireMode ? ' [' + S.fireMode + ']' : '') +
        '\nFPS: ' + S.fpsDisplay + ' PING: ' + Math.round(S.pingVal) + 'ms' + ' TRANSPORT: ' + (getTransportKind() === 'geckos' ? 'WebRTC' : 'WebSocket') +
        '\nPLAYERS: ' + _aliveCount + '/' + S.serverPlayers.length +
        '\nPROJ: ' + S.projData.length +
        netLine +
        iwLine;
    }
  }

  // Minimap — throttled to 10 Hz (shares the _hudTick counter)
  if (S._hudTick === 0) {
    const mc = H.minimap, mctx = mc.getContext('2d');
    mctx.clearRect(0, 0, 120, 90); mctx.fillStyle = 'rgba(0,0,0,0.6)'; mctx.fillRect(0, 0, 120, 90);
    const sx = 120 / MW, sy = 90 / MH;
    for (const p of S.serverPlayers) {
      mctx.fillStyle = p.id === S.myId ? '#ffdd44' : (p.alive ? '#ff88aa' : '#555');
      mctx.fillRect(p.x * sx - 1, p.y * sy - 1, 3, 3);
    }
    mctx.fillStyle = 'rgba(255,255,100,0.4)';
    for (const f of S.serverFoods) { mctx.fillRect(f.x * sx, f.y * sy, 1, 1); }
  }
}
